import { getConfigTenant, isCorpTenant } from "@capone/common";
import { useCorpPersonData } from "@capone/common/src/hooks/useCorpPersonData";
import { ToggleActions } from "halifax";
import isEqual from "lodash-es/isEqual";
import {
  ADD_TRAVELER,
  CallState,
  CorpSessionInfo,
  ICorpPerson,
  IPerson,
  SessionInfo
} from "redmond";
import { all, call, put, select } from "redux-saga/effects";
import fetchUserInfo from "../../api/v1/fetchUserInfo";
import { trackEvent } from "../../api/v1/trackEvent";
import { fetchUserPassengers, IListUserPassengersResponse } from "../../api/v1/user-passengers/fetchUserPassengers";
import fetchCorpUsersList, { CorpUsersListResponse } from "../../api/v1/user/fetchCorpUsersList";
import updateUserDefaultTraveler from "../../api/v1/user/updateUserDefaultTraveler";
import updateUserShowProfile from "../../api/v1/user/updateUserShowProfile";
import updateUserHideProfile from "../../api/v1/user/updateUserHideProfile";
import { IUpdateUserPassenger, setUserPassengers, setUserPassengersCallStateFailed, setUserSelectedPassengerIds } from "../../app/actions";
import { getUserPassengers } from "../../app/selectors";
import { buildCorpTravelers } from "../../utils/capone-corporate/travelersMappingUtils";
import { updateUserPassenger } from "../../api/v1/user-passengers/updateUserPassenger";

export function* updateUserPassengerSaga(action: IUpdateUserPassenger) {
  try {
    // Check if passport field is valid or filter it
    const { person, source, hideProfileAction } = action.payload;
    const { entryPoint } = action;
    const { passport, ...restOfPayload } = person;
    const personToUpdate: IPerson = { ...restOfPayload };
    if (passport?.countryOfIssue?.length === 2) {
      personToUpdate.passport = passport;
    }

    const currentTravelers: IPerson[] | ICorpPerson[] = yield select(
      getUserPassengers
    );
    const existingTraveler = currentTravelers.find(
      (traveler) => traveler.id === personToUpdate.id
    );
    const isNewTraveler: boolean = !existingTraveler;
    const [shouldAssignTraveler, deUserId] = useCorpPersonData(
      "isGhostUser" in person ? (person as ICorpPerson) : null
    );
    // remove undefined keys for comparison
    Object.keys(personToUpdate).forEach(
      (key) => personToUpdate[key] === undefined && delete personToUpdate[key]
    );
    const sameTravelerInfo: boolean = isEqual(existingTraveler, personToUpdate);

    if (!sameTravelerInfo) {
      yield updateUserPassenger({
        person: personToUpdate,
        isNewTraveler,
        source,
        hideProfileAction,
        entryPoint,
        previousHotelLoyalty: existingTraveler?.hotelLoyalty,
      });
    }

    if (shouldAssignTraveler) {
      yield updateUserDefaultTraveler({
        travelerId: personToUpdate.id,
        userId: deUserId,
      });
    }

    if (hideProfileAction === ToggleActions.OFF) {
      yield updateUserShowProfile({
        travelerId: personToUpdate.id,
      });
    } else if (hideProfileAction === ToggleActions.ON) {
      yield updateUserHideProfile({
        travelerId: personToUpdate.id,
      });
    }

    const sessionInfo: SessionInfo = yield call(fetchUserInfo);
    const canViewUsers =
      "corporateInfo" in sessionInfo
        ? (sessionInfo as CorpSessionInfo).corporateInfo.permissions
          .canViewUsers
        : false;

    const travelerDefaultTravelerId =
      ("corporateInfo" in sessionInfo &&
        (sessionInfo as CorpSessionInfo).corporateInfo.defaultTravelerId) ??
      "";

    let userPassengers: IPerson[] | ICorpPerson[];
    if (!isCorpTenant(getConfigTenant())) {
      userPassengers = yield fetchUserPassengers({});
    } else {
      const [corpUsersList, caponeUserPassengers]: [
        CorpUsersListResponse,
        IListUserPassengersResponse
      ] = yield all([
        canViewUsers
          ? call(fetchCorpUsersList)
          : {
            users: [
              {
                id: sessionInfo.userId,
                firstName: sessionInfo.userInfo.firstName,
                lastName: sessionInfo.userInfo.lastName,
                defaultTravelerId: travelerDefaultTravelerId,
              },
            ],
          },
        call(fetchUserPassengers, {}),
      ]);

      const hiddenTravelerIds =
        "corporateInfo" in sessionInfo
          ? (sessionInfo as CorpSessionInfo).corporateInfo.hiddenTravelerIds
          : [];

      const loggedInUserId = sessionInfo.userId;

      userPassengers = buildCorpTravelers(
        caponeUserPassengers,
        corpUsersList.users,
        hiddenTravelerIds,
        loggedInUserId
      );
    }

    if (currentTravelers.length < userPassengers.length) {
      trackEvent({
        eventName: ADD_TRAVELER,
        properties: {},
      });
    }

    yield put(
      setUserPassengers({
        userPassengerCallState: CallState.Success,
        userPassengers,
      })
    );
    yield put(
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: [person?.id],
      })
    );
  } catch (e) {
    yield put(setUserPassengersCallStateFailed());
  }
}
